import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getCanvas as getBarcodeCanvas } from 'pxc-editor-core/helpers/barcode';

import InlineButtonsGroup from '@/components/InlineButtonsGroup';
import CircleRadioButton from '@/components/CircleRadioButton';
import MobileTypingInputToolbar from '@/components/MobileTypingInputToolbar';

import IconExpand from '../../Icon/CmmeIcons/IconExpand';
import IconExpandClose from '../../Icon/CmmeIcons/IconExpandClose';
import IconAdvancedSettings from '../../Icon/CmmeIcons/IconAdvancedSettings';

import IconCmmeCheckbox from '../../Icon/CmmeIcons/IconCmmeCheckbox';
import IconCmmeCheckboxSelected from '../../Icon/CmmeIcons/IconCmmeCheckboxSelected';

import {
  PanelWrapper,
  CodeDataInput,
  InputErrorWrapper,
  CodeSelectWrapper,
  ListWrapper,
  ListItem,
  PanelHeaderWrapper,
  PanelHeader,
  Icon,
  RowWrapper,
  ColWrapper,
  Icon20
} from './styles';
import CodeSelectMenu from '@/components/Menus/CodeSelectMenu/CodeSelectMenu';
import { GridContainer, Label, ToggleButton } from '../styles';

const InsertCodeForm = (props) => {
  const {
    codeGroups = [],
    groupCodes,
    codeSettings,

    codeGroup,
    codeType,
    text,

    compRef,
    onChange,
    onTabApplyClick,

    ...otherOptions
  } = props;

  const [isCodeModalOpen, setCodeModalOpen] = useState(false);
  const [isAdvancedVisible, setAdvancedVisible] = useState(false);
  const [codeTextError, setCodeTextError] = useState('');
  const [inputInFocus, setInputInFocus] = useState(0);
  const prevTextVersion = useRef('');
  const inputInFocusWaitTimeout = useRef();
  const intl = useIntl();
  const codeGroupsList = useMemo(() => (
    codeGroups.map(codeGroupName => ({
      icon: (<FormattedMessage id={"app.editor.sideMenu.panelLabel." + codeGroupName} defaultMessage={codeGroupName} />),
      selected: codeGroup === codeGroupName,
      onClick: () => {
        if (codeGroup !== codeGroupName) {
          onChange({ codeGroup: codeGroupName })
        }
      }
    }))
  ), [codeGroups, codeGroup, onChange]);

  const validateInput = () => {
    let errText = '';
    try {
      // try to build barcode - run validation in library
      getBarcodeCanvas(text, { ...otherOptions, codeType });
    }
    catch (e) {
      const errorString = e.message.split(':')[1].trim();
      errText = errorString[0].toLocaleUpperCase() + errorString.slice(1);
    }
    setCodeTextError(errText);
    return !errText;
  };

  compRef.current = {
    validate: validateInput
  };

  useEffect(() => {
    // delay for waiting of click from MobileTypingInputToolbar
    if (inputInFocus === 2) {
      inputInFocusWaitTimeout.current = setTimeout(() => {
        setInputInFocus(0);
      }, 0);
    }
  }, [inputInFocus]);

  useEffect(() => {
    return () => clearTimeout(inputInFocusWaitTimeout.current)
  }, []);

  const onCodeTypeSelect = useCallback(data => {
    if (data.value !== codeType) {
      onChange({ codeType: data.value })
    }
  }, [codeType, onChange]);

  const onCodeOptionChange = useCallback((key, value) => onChange({ [key]: value }), [onChange]);

  const onMobileTypingInputToolbarCancelClick = () => {
    onChange({ text: prevTextVersion.current });
    setInputInFocus(0);
    prevTextVersion.current = '';
  };

  const [anchorElCodeSelect, setAnchorElCodeSelect] = useState(null);

  const handleClickCodeSelect = (event) => {
    setAnchorElCodeSelect(event.currentTarget);
    setCodeModalOpen(true);
  };

  return (
    <PanelWrapper>
      <CodeSelectMenu
        key="CodeSelectMenu"
        anchorEl={anchorElCodeSelect}
        open={isCodeModalOpen}
        setOpen={setCodeModalOpen}
        selected={codeSettings}
        onSelect={onCodeTypeSelect}
        list={groupCodes}
      />
      <PanelWrapper $gridTemplateColumns="min-content 50px min-content 50px" $rowGap="10px">
        <Label>
          <FormattedMessage id="app.editor.sideMenu.panelLabel.codeData" defaultMessage="Code data" />
        </Label>
        <div className="inputContainerWhite">
          <FormattedMessage id="app.editor.sideMenu.expansionPanel.codeData.example" defaultMessage="Ex.:" >
            {message => (
              <CodeDataInput
                className="inputContainerWhite"
                $error={!!codeTextError}
                placeholder={`${message} ${codeSettings?.example ? intl.formatMessage(codeSettings.example) : ''}`}
                value={text}
                onChange={(e) => {
                  onChange({ text: e.target.value });
                  setCodeTextError('');
                }}
                onFocus={(e) => {
                  e.target.select();
                  prevTextVersion.current = text;

                  setInputInFocus(1);
                }}
                onBlur={() => {
                  setInputInFocus(2);
                }}
              />
            )}
          </FormattedMessage>
        </div>
        <InputErrorWrapper>{codeTextError}</InputErrorWrapper>

        <div className="inputContainerWhite">
          <InlineButtonsGroup
            $backgroundColor="transparent"
            items={codeGroupsList}
          />
        </div>
      </PanelWrapper>
      <PanelWrapper>
        <GridContainer key="gridControlTools-grid" $gridTemplateColumns="1fr">
          <Label>
            <FormattedMessage id="app.editor.sideMenu.panelLabel.codeFormat" defaultMessage="Code format" />
          </Label>
          <CodeSelectWrapper onClick={(e) => handleClickCodeSelect(e)} >
            <Label>{codeSettings?.label ? intl.formatMessage(codeSettings.label) : ''}</Label>
            {isCodeModalOpen ? <IconExpandClose /> : <IconExpand />}
          </CodeSelectWrapper>
        </GridContainer>

        {codeSettings?.options?.basic && (
          <>
            <MapBasicOptions
              settings={codeSettings.options.basic}
              data={otherOptions}
              onChange={onCodeOptionChange}
            />
          </>
        )}
      </PanelWrapper>
      <PanelWrapper>
        {codeSettings?.options?.advanced && Object.keys(codeSettings.options.advanced).length > 0 && (
          <>
            <GridContainer key="extendedSettings-checkbox-grid" $gridTemplateColumns="1fr">
              <CheckboxSelect
                labelId={"app.editor.sideMenu.panelLabel.extendedSettings"}
                displayName={"Advanced settings"}
                selected={isAdvancedVisible}
                onSelect={() => setAdvancedVisible(!isAdvancedVisible)}
                isToggleButton={true}
                Icon={IconAdvancedSettings}
              />
            </GridContainer>
            {isAdvancedVisible && (
              <GridContainer key="extendedSettings-grid" $gridTemplateColumns="1fr">
                <MapAdvancedOptions
                  settings={codeSettings.options.advanced}
                  data={otherOptions}
                  onChange={onCodeOptionChange}
                />
              </GridContainer>
            )}
          </>
        )}
      </PanelWrapper>
      {!!inputInFocus && (
        <MobileTypingInputToolbar onCancelClick={onMobileTypingInputToolbarCancelClick} />
      )}
    </PanelWrapper>
  );
};

export default React.memo(InsertCodeForm);

const MapBasicOptions = React.memo(({ settings, data, onChange }) => {
  const alignmentKeys = Object.keys(settings).filter(key => key === 'textxalign' || key === 'textyalign');
  const includetextKeys = Object.keys(settings).filter(key => key === 'includetext');
  const includecheckKeys = Object.keys(settings).filter(key => key === 'includecheck');
  const ecLevelKeys = Object.keys(settings).filter(key => key === 'eclevel');
  let isTextIncluded = false;
  
  return (
    <>
      {
        !!ecLevelKeys.length && (
          <>
            <RowWrapper key={'barcode-aligment-container'} $gridTemplateColumns="1fr">
              {
                ecLevelKeys.map(key => {
                  const { displayName, values, defaultValueIndex, labelId, inputType } = settings[key];
                  const selected = (data || {})[key];
                  return (
                    <ColWrapper key={`${key}-input`} $gridTemplateRows="min-content 1fr">
                      <OptionLabel labelId={labelId} displayName={displayName} />
                      <TabsSelect
                        id={key}
                        values={values}
                        selected={selected}
                        onSelect={onChange}
                      />
                    </ColWrapper>
                  )
                })
              }
            </RowWrapper>
          </>
        )
      }
      {
        !!includetextKeys.length && (
          <>
            <RowWrapper key={'barcode-includetext-container'} $gridTemplateColumns="1fr">
              {
                includetextKeys.map(key => {
                  const { displayName, labelId, values } = settings[key];
                  const selected = (data || {})[key];
                  isTextIncluded = selected;
                  return (
                    <CheckboxSelect
                      className='checkBoxContainer'
                      key={`${key}-input`}
                      id={key}
                      labelId={labelId}
                      displayName={displayName}
                      selected={selected}
                      onSelect={onChange}
                      isToggleButton={true}
                      Icon={values[0]?.Icon}
                      IconSelected={values[0]?.IconSelected}
                    />
                  )
                })
              }
            </RowWrapper>
            {
              !!alignmentKeys.length && isTextIncluded && (
                <RowWrapper key={'barcode-aligment-container'} $gridTemplateColumns="1fr 1fr">
                  {
                    alignmentKeys.map(key => {
                      const { displayName, values, defaultValueIndex, labelId, inputType } = settings[key];
                      const selected = (data || {})[key];
                      return (
                        <ColWrapper key={`${key}-input`} $gridTemplateRows="min-content 1fr">
                          <OptionLabel labelId={labelId} displayName={displayName} />
                          <TabsSelect
                            id={key}
                            values={values}
                            selected={selected}
                            onSelect={onChange}
                          />
                        </ColWrapper>
                      )
                    })
                  }
                </RowWrapper>
              )
            }
            {!!includecheckKeys.length && (
              <RowWrapper key={'barcode-includecheck-container'} $gridTemplateColumns="1fr">
                {
                  includecheckKeys.map(key => {
                    const { displayName, values, defaultValueIndex, labelId, inputType } = settings[key];
                    const selected = (data || {})[key];
                    isTextIncluded = selected;
                    return (
                      <CheckboxSelect
                        className='checkBoxContainer'
                        key={`${key}-input`}
                        id={key}
                        labelId={labelId}
                        displayName={displayName}
                        selected={selected}
                        onSelect={onChange}
                      />
                    )
                  })
                }
              </RowWrapper>
            )}
          </>
        )
      }
    </>
  )
});

const MapAdvancedOptions = React.memo(({ settings, data, onChange }) => {
  const optionsKeys = Object.keys(settings);
  return optionsKeys.map(key => {
    const { displayName, values, defaultValueIndex, labelId, inputType } = settings[key];
    const selected = (data || {})[key];
    switch (inputType) {
      case 'tabs':
        return (
          <React.Fragment key={`${key}-input`}>
            <OptionLabel labelId={labelId} displayName={displayName} />
            <TabsSelect
              id={key}
              values={values}
              selected={selected}
              onSelect={onChange}
            />
          </React.Fragment>
        );
      case 'checkbox':
        return (
          <CheckboxSelect
            className='checkBoxContainer'
            key={`${key}-input`}
            id={key}
            labelId={labelId}
            displayName={displayName}
            selected={selected}
            onSelect={onChange}
          />
        );
      default:
        return (
          <React.Fragment key={`${key}-input`}>
            <OptionLabel labelId={labelId} displayName={displayName} />
            <RadioButtonsSelect
              id={key}
              values={values}
              selected={selected}
              onSelect={onChange}
            />
          </React.Fragment>
        );
    }
  });
});

const RadioButtonsSelect = React.memo(({ id, values, selected, onSelect }) => (
  <ListWrapper>
    {values.map(({ value, label, labelId }) => (
      <ListItem key={`${id}-${value}`} onClick={() => onSelect(id, value)} >
        <CircleRadioButton selected={selected === value} />
        <Label>
          {labelId ? (<FormattedMessage id={labelId} defaultMessage={label} />) : (label)}
        </Label>
      </ListItem>
    ))}
  </ListWrapper>
));

const TabsSelect = React.memo(({ id, values, selected, onSelect }) => (
  <div className='inputContainerWhite'>
    <InlineButtonsGroup style={{ backgroundColor: '#ffffff' }}
      height="49px"
      items={values.map(({ value, Icon: IconComp, IconSelected: IconCompSelected, label, labelId }) => {
        const iconVal = IconComp
          ? <Icon20 src={value === selected ? IconCompSelected : IconComp}
            onMouseOver={(e) => e.target.src = IconCompSelected}
            onMouseOut={(e) => e.target.src = value === selected ? IconCompSelected : IconComp}
          />
          : labelId ? (<FormattedMessage id={labelId} defaultMessage={label} />) : (label);
        return {
          icon: iconVal,
          selected: value === selected,
          onClick: () => onSelect(id, value),
        }
      })}
    />
  </div>
));

const CheckboxSelect = React.memo(({ id, labelId, Icon: IconComp, IconSelected: IconCompSelected, displayName, selected, onSelect, isToggleButton }) => (
  <>
    {
      isToggleButton ?
        <GridContainer key="gridControlTools-grid" $gridTemplateColumns="1fr 1fr">
          <PanelHeader>
            <OptionLabel labelId={labelId} displayName={displayName} />
          </PanelHeader>
          <div></div>
          <div className="inputContainerWhite" onClick={() => onSelect(id, !selected)}>
            <ToggleButton $selected={selected}>
              {
                IconCompSelected ?
                  <Icon src={selected ? IconCompSelected : IconComp}
                    onMouseOver={(e) => e.target.src = IconCompSelected}
                    onMouseOut={(e) => e.target.src = selected ? IconCompSelected : IconComp}
                  />
                  :
                  <IconComp fill={selected ? '#007e83' : '#202020'} />
              }
            </ToggleButton >
          </div>
          <div></div>
        </GridContainer>
        :
        <GridContainer key="gridControlTools-grid" $gridTemplateColumns="1fr">
          <PanelHeaderWrapper onClick={() => onSelect(id, !selected)} >
            {
              selected ?
                <IconCmmeCheckboxSelected fill="#007e83" />
                :
                <IconCmmeCheckbox />
            }

            <PanelHeader>
              <OptionLabel labelId={labelId} displayName={displayName} />
            </PanelHeader>
          </PanelHeaderWrapper>
        </GridContainer>
    }
  </>
));

const OptionLabel = React.memo(({ labelId, displayName }) => (
  <Label>
    {labelId
      ? (<FormattedMessage id={labelId} defaultMessage={displayName} />)
      : displayName
    }
  </Label>
));
